import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { Button } from '@salutejs/sdds-insol';
import { useUserStore } from '@/entities/User';
import { Typography } from '@/shared/ui/Typography';
import { zodResolver } from '@hookform/resolvers/zod';
import { prepareDate } from '@/shared/lib/utils/auxiliaryFuncitions';
import { useTimeSelectData } from '@/shared/lib/hooks/useTimeSelectData';
import { TIME_SELECT_DATA, TODAY, TODAY_PLUS_TWO_MONTHS } from '@/shared/const';
import { DatePicker, NumberFormat, Select, TextField } from '@/features/FormFields';
import { useDepositQuery } from '../query/useDepositQuery';
import { DepositSchema, type DepositSchemaType } from '../model/schema';
import cls from './DepositForm.module.scss';

const StyledButton = styled(Button)`
    background-color: #333f48;
`;

export const DepositForm = () => {
    const {
        userData: { firstName, secondName, middleName, phoneNumber, email },
        user: { agreemtId },
    } = useUserStore();

    const methods = useForm<DepositSchemaType>({
        defaultValues: {
            fio: `${secondName} ${firstName} ${middleName}`,
            phone: phoneNumber,
            email,
            insurancePayment: '',
            depositDate: '',
            depositTime: '',
        },
        resolver: zodResolver(DepositSchema),
        mode: 'onSubmit',
    });

    const { handleSubmit, getValues, watch, setValue } = methods;

    const timeValue = TIME_SELECT_DATA.find((el) => el.value === watch('depositTime'))?.label || '';

    const { mutate: deposit, isPending } = useDepositQuery({
        date: watch('depositDate') as string,
        time: timeValue,
    });

    const submitHandler = async (data: DepositSchemaType) => {
        const { depositDate, depositTime, email, fio, insurancePayment, phone } = data;

        const callDate = prepareDate(depositDate);
        const callTime = TIME_SELECT_DATA.find((el) => el.value === depositTime)?.label || '';

        deposit({
            callDate,
            callTime,
            email,
            fio,
            insurancePayment,
            phone,
            agreemtId,
        });
    };

    const { timeSelectData, disabledDays } = useTimeSelectData({ getValues, setValue, watch });

    return (
        <article className={cls.container} id="depositForm">
            <FormProvider {...methods}>
                <div className={cls.title}>
                    <Typography className={cls.titleText} as="h6" variant="h6_bold">
                        Заполните данные
                    </Typography>
                    <Typography as="p" variant="text_16">
                        Наш менеджер свяжется с вами
                        <br />в указанную дату
                    </Typography>
                </div>
                <form className={cls.form} id="ConsultationForm" onSubmit={handleSubmit(submitHandler)}>
                    <TextField size="l" name="fio" label="ФИО" disabled />
                    <TextField size="l" name="phone" label="Номер телефона" disabled />
                    <TextField size="l" name="email" label="Почта" disabled />
                    <NumberFormat size="l" decimalScale={0} label="Страховой взнос" name="insurancePayment" />
                    <DatePicker
                        className={cls.date}
                        size="l"
                        min={TODAY}
                        name="depositDate"
                        label="Выберите дату"
                        max={TODAY_PLUS_TWO_MONTHS}
                        disabledDays={disabledDays}
                    />
                    <Select className={cls.time} size="l" name="depositTime" label="Выберите время" items={timeSelectData} />
                    <StyledButton
                        className={cls.button}
                        size="m"
                        view="dark"
                        type="submit"
                        disabled={isPending}
                        isLoading={isPending}
                        stretch
                    >
                        <span className="SBSansText">Отправить</span>
                    </StyledButton>
                </form>
            </FormProvider>
        </article>
    );
};
